import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ImageOff, MapPin, Shirt, ScanFace, Phone, Users, Flag, Printer, ArrowLeft } from "lucide-react";
import QRCode from "react-qr-code";
import { api, photoSrc } from "../lib/api";
import { TypeBadge, MinorBadge, StatusBadge } from "../components/Badges";

export default function CaseDetail() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [c, setC] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, noarchive, nofollow";
    document.head.appendChild(meta);
    return () => { document.head.removeChild(meta); };
  }, []);

  useEffect(() => {
    api.get(`/cases/${code}`)
      .then((res) => setC(res.data))
      .catch((e) => setError(e.response?.data?.detail || "Caso no disponible"))
      .finally(() => setLoading(false));
  }, [code]);

  if (loading) return <div className="text-[#475569]">Cargando…</div>;
  if (error) return <div className="text-center py-12 text-[#475569]" data-testid="case-detail-error">{error}</div>;

  const src = photoSrc(c.photo_url);
  const name = [c.first_name, c.last_name].filter(Boolean).join(" ") || "Sin nombre";
  const caseUrl = `${window.location.origin}/caso/${c.public_case_code}`;

  return (
    <div className="space-y-5">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-[#475569]" data-testid="case-back-btn">
        <ArrowLeft className="w-4 h-4" /> Volver
      </button>

      <div className="bg-white border border-[#E2E8F0] rounded-2xl overflow-hidden">
        <div className="aspect-[4/3] sm:aspect-[16/9] bg-[#F1F5F9] flex items-center justify-center overflow-hidden">
          {src ? <img src={src} alt={name} className="w-full h-full object-cover" data-testid="case-photo" />
               : <ImageOff className="w-12 h-12 text-[#CBD5E1]" />}
        </div>
        <div className="p-5 space-y-4">
          <div className="flex items-center gap-2 flex-wrap">
            <TypeBadge caseType={c.case_type} label={c.case_type_label} />
            {c.is_minor_case && <MinorBadge />}
            <StatusBadge label={c.status_label} />
          </div>

          <div>
            <h1 className="font-heading text-2xl font-bold text-[#0F172A]" data-testid="case-name">{name}</h1>
            {c.nickname && <p className="text-[#475569]">Apodo: {c.nickname}</p>}
          </div>

          <dl className="grid grid-cols-2 gap-3 text-sm">
            <Field label="Edad aproximada" value={c.approximate_age != null ? `${c.approximate_age} años` : c.age_range} />
            <Field label="Género" value={sexLabel(c.sex)} />
            <Field label="Zona aproximada" value={c.approximate_zone} icon={MapPin} />
            <Field label="Fecha aproximada" value={c.approximate_date} />
          </dl>

          {c.clothing_description && <DescBlock icon={Shirt} title="Ropa" text={c.clothing_description} />}
          {c.distinguishing_marks && <DescBlock icon={ScanFace} title="Señales particulares" text={c.distinguishing_marks} />}
          {c.physical_description && <DescBlock title="Descripción física" text={c.physical_description} />}
          {c.public_location_label && <DescBlock icon={MapPin} title="Ubicación (referencia)" text={c.public_location_label} />}

          <div className="flex items-center justify-between gap-4 pt-2 border-t border-[#E2E8F0]">
            <div>
              <p className="text-xs uppercase tracking-wide font-semibold text-[#94A3B8]">Código de caso</p>
              <p className="font-mono font-semibold text-[#0F172A]" data-testid="case-code">{c.public_case_code}</p>
            </div>
            <div className="bg-white p-1.5 rounded-lg border border-[#E2E8F0]">
              <QRCode value={caseUrl} size={72} />
            </div>
          </div>
        </div>
      </div>

      <div className="grid gap-3 no-print">
        <Link to={`/caso/${c.public_case_code}/contacto?tipo=have_information`} data-testid="case-have-info-btn"
          className="flex items-center justify-center gap-2 h-14 rounded-xl bg-[#1E3A8A] text-white font-semibold hover:bg-[#1E40AF]">
          <Phone className="w-5 h-5" /> Tengo información
        </Link>
        <Link to={`/caso/${c.public_case_code}/contacto?tipo=think_relative`} data-testid="case-relative-btn"
          className="flex items-center justify-center gap-2 h-14 rounded-xl bg-white border border-[#E2E8F0] text-[#0F172A] font-semibold hover:bg-[#F8FAFC]">
          <Users className="w-5 h-5" /> Creo que es mi familiar
        </Link>
        <div className="flex gap-3">
          <Link to={`/caso/${c.public_case_code}/imprimir`} data-testid="case-print-btn"
            className="flex-1 flex items-center justify-center gap-2 h-12 rounded-xl bg-[#F1F5F9] text-[#0F172A] font-medium hover:bg-[#E2E8F0]">
            <Printer className="w-4 h-4" /> Ficha imprimible
          </Link>
          <Link to={`/caso/${c.public_case_code}/abuso`} data-testid="case-abuse-btn"
            className="flex-1 flex items-center justify-center gap-2 h-12 rounded-xl bg-[#FEF2F2] text-[#991B1B] font-medium hover:bg-[#FEE2E2]">
            <Flag className="w-4 h-4" /> Reportar abuso
          </Link>
        </div>
      </div>
    </div>
  );
}

function sexLabel(s) {
  return { male: "Masculino", female: "Femenino", other: "Otro", unspecified: "No especificado", unknown: "No especificado" }[s] || "—";
}

function Field({ label, value, icon: Icon }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide font-semibold text-[#94A3B8]">{label}</dt>
      <dd className="text-[#0F172A] flex items-center gap-1">{Icon && <Icon className="w-3.5 h-3.5 text-[#94A3B8]" />}{value || "—"}</dd>
    </div>
  );
}

function DescBlock({ icon: Icon, title, text }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wide font-semibold text-[#94A3B8] flex items-center gap-1">
        {Icon && <Icon className="w-3.5 h-3.5" />} {title}
      </p>
      <p className="text-sm text-[#0F172A] leading-relaxed">{text}</p>
    </div>
  );
}
